// exam.js: assembles a full mock exam from the flattened by-section pool (generated by
// scripts/build-registry.js) on the real PPL 20/20/30/30 section split (PLAN §5), and scores
// it against the real pass rules (60% overall AND 60% in every section). Pure: the whole exam
// is a function of one master seed, so it can be replayed or shared by seed (PLAN §5, §9).

import { drawSet, instanceSeed } from './draw.js';
import { GENERATORS } from '../../_generated/exam-pool.js';

/** @typedef {import('./contract.js').Question} Question */

export const SECTION_COUNTS = { 'air-law': 20, navigation: 20, meteorology: 30, aeronautics: 30 };
export const PASS_PCT = 60;

const bySection = (gens) => {
  const map = {};
  for (const g of gens) (map[g.section] ??= []).push(g);
  return map;
};

/**
 * Build an exam: for each section, draw its target count of distinct instances from that
 * section's generators (drawSet round-robins + dedupes). Each section gets its own sub-seed
 * off the master so sections don't share a stream, keeping the whole exam reproducible.
 * @param {number} masterSeed
 * @param {Record<string, number>} [counts]
 * @returns {{ seed: number, questions: Question[] }}
 */
export function buildExam(masterSeed, counts = SECTION_COUNTS) {
  const pools = bySection(GENERATORS);
  const questions = [];
  let s = 0;
  for (const [section, target] of Object.entries(counts)) {
    const pool = pools[section] ?? [];
    if (pool.length === 0) continue; // no generators for this section yet
    questions.push(...drawSet(pool, target, instanceSeed(masterSeed, s++)));
  }
  return { seed: masterSeed, questions };
}

/**
 * Score an exam. `answers[i]` is the chosen option index for question i (null = unanswered).
 * @param {Question[]} questions
 * @param {(number|null)[]} answers
 * @returns {{ correct:number, total:number, overallPct:number, pass:boolean,
 *   sections: Record<string,{correct:number,total:number,pct:number,pass:boolean}> }}
 */
export function scoreExam(questions, answers) {
  const sections = {};
  questions.forEach((q, i) => {
    const s = (sections[q.section] ??= { correct: 0, total: 0, pct: 0, pass: false });
    s.total++;
    if (answers[i] === q.answer) s.correct++;
  });
  const pct = (c, t) => (t ? (100 * c) / t : 0);
  let correct = 0;
  for (const s of Object.values(sections)) {
    correct += s.correct;
    s.pct = pct(s.correct, s.total);
    s.pass = s.pct >= PASS_PCT;
  }
  const total = questions.length;
  const overallPct = pct(correct, total);
  const pass = overallPct >= PASS_PCT && Object.values(sections).every((s) => s.pass);
  return { correct, total, overallPct, pass, sections };
}
